import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { eventAPI } from '../lib/eventApi';
import RegistrationSetupTab from '../components/events/RegistrationSetupTab';
import WeekManagementTab from '../components/events/WeekManagementTab';
import StandingsPanel from '../components/events/StandingsPanel';
import EditSessionDialog from '../components/events/EditSessionDialog';
import DeleteSessionDialog from '../components/events/DeleteSessionDialog';
import EventManagerAssignment from '../components/events/EventManagerAssignment';
import CommunicateCard from '../components/events/CommunicateCard';
import EventMediaCard from '../components/events/EventMediaCard';
import { ArrowLeft, CalendarBlank, ChartBar, ClipboardText, PencilSimple, Trash, ShieldCheck } from '@phosphor-icons/react';

const POST_START_STATUSES = new Set(['in_progress', 'completed']);

export default function AdminEventEditorPage() {
  const { sessionId } = useParams();
  const { user, managedSessions } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSession] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await eventAPI.getSession(sessionId);
      setSession(r.data.session);
      setRegistrations(r.data.registrations || []);
      setError(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load session');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  // EM-3 page-level guard. Admin always passes; otherwise the user must
  // appear in `managedSessions` for THIS specific session_id. Backend
  // already enforces (EM-2) but we short-circuit the UI to avoid showing
  // a broken editor with 403s on every endpoint.
  const isAdmin = user?.role === 'admin';
  const isManager = Array.isArray(managedSessions) && managedSessions.includes(sessionId);
  if (!isAdmin && !isManager) return <Navigate to="/" replace />;

  if (loading) {
    return (
      <div className="space-y-4" data-testid="event-editor-loading">
        <div className="h-8 w-40 bg-[#f0ece3] rounded animate-pulse" />
        <div className="h-24 bg-[#f0ece3] rounded-2xl animate-pulse" />
        <div className="h-32 bg-[#f0ece3] rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="space-y-4" data-testid="event-editor-error">
        <button
          onClick={() => navigate('/admin/events')}
          className="text-sm text-[#015e3f] flex items-center gap-1"
        >
          <ArrowLeft size={14} /> Back to events
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {error || 'Session not found'}
        </div>
      </div>
    );
  }

  const isStarted = POST_START_STATUSES.has(session.status);
  const isCompleted = session.status === 'completed';
  const defaultTab = isCompleted ? 'standings' : isStarted ? 'week' : 'setup';
  const activeTab = searchParams.get('view') || defaultTab;

  const setActiveTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === defaultTab) next.delete('view');
    else next.set('view', id);
    setSearchParams(next, { replace: true });
  };

  const tabs = isStarted
    ? [
        { id: 'week', label: 'Week', icon: CalendarBlank },
        { id: 'standings', label: 'Standings', icon: ChartBar },
        { id: 'setup', label: 'Setup', icon: ClipboardText },
      ]
    : [
        { id: 'setup', label: 'Setup', icon: ClipboardText },
      ];

  return (
    <div className="space-y-6" data-testid="event-editor-page">
      <button
        onClick={() => navigate('/admin/events')}
        className="text-sm text-[#015e3f] flex items-center gap-1"
        data-testid="back-to-events-btn"
      >
        <ArrowLeft size={14} /> Back to events
      </button>

      <header>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight" data-testid="editor-session-name">
              {session.name}
            </h1>
            <div className="mt-1 flex flex-wrap gap-2 text-xs text-[#6a766e]">
              <span className="px-2 py-0.5 rounded-full bg-[#f0ece3] text-[#013523]">
                {session.gender === 'mens' ? "Men's Night" : 'Ladies Night'}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-[#f0ece3] text-[#013523]">
                {session.num_courts} courts × {session.players_per_court}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-[#f0ece3] text-[#013523]">
                {session.weeks_planned} weeks
              </span>
              <span
                className="px-2 py-0.5 rounded-full bg-[#015e3f]/10 text-[#015e3f] font-medium"
                data-testid="editor-session-status"
              >
                {session.status.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
          {!isStarted && (
            <div className="shrink-0 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                data-testid="editor-edit-session-btn"
                onClick={() => setEditOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e3dcd1] text-xs text-[#013523] hover:bg-[#f0ece3]"
              >
                <PencilSimple size={14} weight="bold" /> Edit
              </button>
              {/* Delete is admin-only — Event Managers cannot delete the
                  session they manage (matches backend EM-2 enforcement). */}
              {isAdmin && (
                <button
                  type="button"
                  data-testid="editor-delete-session-btn"
                  onClick={() => setDeleteOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-xs text-red-700 hover:bg-red-50"
                >
                  <Trash size={14} weight="bold" /> Delete
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {/* EM-3a — Admin-only panel to assign/remove Event Managers.
          Placed IMMEDIATELY below the header so admins on a long
          started-session page (schedule + week + standings) don't have
          to scroll past the whole roster to find it. The component
          renders null for non-admins; we still gate at the top-level
          here to keep the DOM tidy. */}
      {isAdmin && (
        <EventManagerAssignment eventKind="event_session" eventId={sessionId} isAdmin={isAdmin} />
      )}

      {isAdmin && (
        <CommunicateCard eventKind="event_session" eventId={sessionId} eventName={session.name} isAdmin={isAdmin} registeredCount={registrations.length} />
      )}

      {isAdmin && (
        <EventMediaCard
          kind="session"
          eventId={sessionId}
          eventName={session.name}
          flyerUrl={session.flyer_url}
          featuredOnHome={session.featured_on_home}
          onChange={load}
        />
      )}

      {/* EM-3 — banner shown only when the viewer is NOT a full admin but
          IS an assigned Event Manager. Tells them their scope is just this
          session. Admins never see this banner. */}
      {!isAdmin && isManager && (
        <div
          className="flex items-start gap-3 bg-[#013523] text-white rounded-xl px-4 py-3"
          data-testid="event-manager-banner"
        >
          <div className="shrink-0 w-8 h-8 rounded-lg bg-[#c2a251]/15 text-[#c2a251] flex items-center justify-center ring-1 ring-[#c2a251]/30">
            <ShieldCheck size={16} weight="fill" />
          </div>
          <div className="text-sm leading-relaxed">
            <div className="font-medium">You&apos;re managing this session as Event Manager.</div>
            <p className="text-white/75 text-xs mt-0.5">
              You can edit the roster, capacity, weekly scores, and start/finalize this session.
              Other admin tools are not available.
            </p>
          </div>
        </div>
      )}

      {isStarted && (
        <div className="flex gap-1 bg-[#f0ece3] p-1 rounded-xl overflow-x-auto" data-testid="editor-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`editor-tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[11px] font-medium transition-all whitespace-nowrap ${
                activeTab === t.id
                  ? 'bg-[#fbfaf8] text-[#015e3f] shadow-sm'
                  : 'text-[#6a766e] hover:text-[#013523]'
              }`}
            >
              <t.icon size={14} weight={activeTab === t.id ? 'fill' : 'regular'} />
              {t.label}
            </button>
          ))}
        </div>
      )}

      {!isStarted && (
        <RegistrationSetupTab
          session={session}
          registrations={registrations}
          onChange={load}
          isAdmin={isAdmin}
        />
      )}

      {isStarted && activeTab === 'week' && (
        <WeekManagementTab session={session} onSessionChange={load} />
      )}
      {isStarted && activeTab === 'standings' && (
        <StandingsPanel sessionId={session.id} refreshKey={refreshKey} />
      )}
      {isStarted && activeTab === 'setup' && (
        <RegistrationSetupTab
          session={session}
          registrations={registrations}
          onChange={load}
          isAdmin={isAdmin}
        />
      )}

      {/* EM-3a duplicate removed — the section is now rendered above,
          immediately below the page header, so admins see it first. */}

      <EditSessionDialog
        open={editOpen}
        session={session}
        registrations={registrations}
        onClose={() => setEditOpen(false)}
        onSaved={load}
      />

      <DeleteSessionDialog
        open={deleteOpen}
        session={session}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false);
          navigate('/admin/events');
        }}
      />
    </div>
  );
}
